import React from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as AppAction from '../../../redux/actions/appAction';
import * as JobActions from '../../../redux/actions/jobAction';
import { View } from 'react-native';
import { Button, HStack, Modal, Text, useToast } from 'native-base';
import MaterialIconByName from 'src/components/MaterialIconByName';
import { IStore } from 'src/redux/reducers';
import { ISearchQuery, IDateRange, IJobRequest } from 'src/models';
import JobFilters from 'src/components/JobFilters';
import { getText } from 'src/utils/i18n';
import { validateJobsForAveraging } from 'src/utils/jobUtils';
import uuid from 'react-native-uuid';
import { FEATURE_FLAGS } from 'src/constants';
import { isElectron, shouldUseLocalProcessing } from 'src/utils/miscUtils';
import ActiveJobFilterChips from 'src/components/ActiveJobFilterChips';

export default function FilterModal() {

    const [filterModalVisible, setFilterModalVisible] = React.useState(false);
    const [numberOfFiltersApplied, setNumberOfFiltersApplied] = React.useState(0);
    const isAveragingEnabled = FEATURE_FLAGS.ENABLE_GRAIN_AVERAGING;

    const filterSnapshot = React.useRef<{
        grainIdFilter?: string;
        searchQuery?: ISearchQuery;
        dateRange?: IDateRange;
        stateFilter?: string;
        districtFilter?: string;
        centerIdFilter?: string;
    } | null>(null);

    const dispatch = useDispatch();
    const toast = useToast();

    // Averaging feature selectors
    const isSelectionMode = useSelector((store: IStore) => store.job.isSelectionMode);
    const selectedJobIds = useSelector((store: IStore) => store.job.selectedJobIds);
    const userJobs = useSelector((store: IStore) => store.job.userJobs.jobs);
    const isCreatingJob = useSelector((store: IStore) => store.job.createJob.isFetching);
    const createJobSuccess = useSelector((store: IStore) => store.job.createJob.success);
    const createJobError = useSelector((store: IStore) => store.job.createJob.error);
    const createJobSource = useSelector((store: IStore) => store.job.createJob.source);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);

    // Track if we've shown the toast for this job creation cycle
    const hasShownSuccessToast = React.useRef(false);
    const hasShownErrorToast = React.useRef(false);

    // Validation for averaging
    const validationResult = React.useMemo(() => {
        return validateJobsForAveraging(
            selectedJobIds,
            userJobs?.Items || []
        );
    }, [selectedJobIds, userJobs]);

    // Handle averaging job creation
    const handleAverage = () => {
        // Re-validate before creating job to prevent race conditions
        const currentValidation = validateJobsForAveraging(
            selectedJobIds,
            userJobs?.Items || []
        );

        if (!currentValidation.isValid) {
            toast.show({
                description: currentValidation.errorMessage || 'Invalid job selection',
                duration: 3000,
                bg: 'error.600'
            });
            return;
        }

        const selectedJobs = userJobs?.Items.filter(job =>
            selectedJobIds.includes(job.jobId)
        );

        if (!selectedJobs || selectedJobs.length !== selectedJobIds.length) {
            toast.show({
                description: 'Some selected jobs are not available locally',
                duration: 3000,
                bg: 'error.600'
            });
            return;
        }

        if (selectedJobs.length > 0) {
            const jobId = `avg-${uuid.v4()}`;
            const request: IJobRequest = {
                jobId: jobId,
                source: 'averaging',
                selectedJobs: selectedJobIds,
                grainId: selectedJobs[0].grainId,
                profileId: selectedJobs[0].profileId
            };

            dispatch(JobActions.setJobRequest(request));
            if (isElectron() && shouldUseLocalProcessing(preferredProcessingMode, isOnline)) {
                dispatch(JobActions.processLocalAveragingJob.request({
                    jobId,
                    selectedJobs: selectedJobIds,
                    grainId: selectedJobs[0].grainId,
                    profileId: selectedJobs[0].profileId || '',
                    location: 'app',
                    additionalAttributes: {
                        sourceJobIds: selectedJobIds,
                    }
                }));
            } else {
                dispatch(JobActions.createJob.request({
                    jobId: jobId,
                    source: 'averaging',
                    location: 'app'
                }));
            }
        }
    };

    // Reset toast flags when job creation starts
    React.useEffect(() => {
        if (isCreatingJob && createJobSource === 'averaging') {
            hasShownSuccessToast.current = false;
            hasShownErrorToast.current = false;
        }
    }, [isCreatingJob, createJobSource]);

    // Handle API response for job creation
    React.useEffect(() => {
        if (createJobSource === 'averaging' && createJobSuccess && !isCreatingJob && !hasShownSuccessToast.current) {
            hasShownSuccessToast.current = true;
            toast.show({
                description: getText('averagingJobCreated'),
                duration: 3000,
                bg: 'success.600'
            });
            dispatch(JobActions.clearSelection());
            dispatch(JobActions.toggleSelectionMode());
        }
    }, [createJobSuccess, isCreatingJob, createJobSource, dispatch, toast]);

    React.useEffect(() => {
        if (createJobSource === 'averaging' && createJobError && !isCreatingJob && !hasShownErrorToast.current) {
            hasShownErrorToast.current = true;
            toast.show({
                description: getText('failedToCreateJob'),
                duration: 3000,
                bg: 'error.600'
            });
        }
    }, [createJobError, isCreatingJob, createJobSource, toast]);

    React.useEffect(() => {
        if (!isAveragingEnabled && isSelectionMode) {
            dispatch(JobActions.clearSelection());
            dispatch(JobActions.toggleSelectionMode());
        }
    }, [dispatch, isAveragingEnabled, isSelectionMode]);

    const handleReset = () => {
        if (isSelectionMode) {
            // Exit selection mode and clear selections
            dispatch(JobActions.clearSelection());
            dispatch(JobActions.toggleSelectionMode());
        } else {
            // Reset filters
            dispatch(AppAction.resetFilters());
        }
    };

    const grainIdFilter = useSelector((store: IStore) => store.app.grainIdFilter) || "all";
    const searchQuery: ISearchQuery | undefined = useSelector((store: IStore) => store.app.searchQuery);
    const dateRange: IDateRange | undefined = useSelector((store: IStore) => store.app.dateRange);
    const stateFilter = useSelector((store: IStore) => store.app.stateFilter);
    const districtFilter = useSelector((store: IStore) => store.app.districtFilter);

    const centerIdFilter = useSelector((store: IStore) => store.app.centerIdFilter) || "all";

    const showFilterModal = () => {
        filterSnapshot.current = {
            grainIdFilter,
            searchQuery,
            dateRange,
            stateFilter,
            districtFilter,
            centerIdFilter,
        };
        setFilterModalVisible(true);
    };

    const saveAndHideFilterModal = () => {
        filterSnapshot.current = null;
        setFilterModalVisible(false);
    };

    const cancelAndRestoreFilters = () => {
        const snapshot = filterSnapshot.current;
        if (snapshot) {
            dispatch(AppAction.setGrainIdFilter(snapshot.grainIdFilter));
            dispatch(AppAction.setSearchQuery(snapshot.searchQuery));
            dispatch(AppAction.setDateRange(snapshot.dateRange));
            dispatch(AppAction.setStateFilter(snapshot.stateFilter));
            dispatch(AppAction.setDistrictFilter(snapshot.districtFilter));
            dispatch(AppAction.setCenterIdFilter(snapshot.centerIdFilter));
        }
        filterSnapshot.current = null;
        setFilterModalVisible(false);
    };



    const getNumberOfFiltersApplied = () => {
        let count = 0;
        if (grainIdFilter !== undefined
            && grainIdFilter !== ""
            && grainIdFilter !== "0"
            && grainIdFilter !== "all") {
            count++;
        }
        if (searchQuery !== undefined && searchQuery?.query !== undefined && searchQuery?.query !== "") {
            count++;
        }
        if (dateRange !== undefined && dateRange?.from !== undefined && dateRange?.to !== undefined) {
            count++;
        }

        if (centerIdFilter !== ""
            && centerIdFilter !== undefined
            && centerIdFilter !== "all") {
            count++;
        }

        if (stateFilter !== ""
            && stateFilter !== undefined
            && stateFilter !== "all") {
            count++;
        }

        if (districtFilter !== ""
            && districtFilter !== undefined
            && districtFilter !== "all") {
            count++;
        }

        return count;
    }

    React.useEffect(() => {
        setNumberOfFiltersApplied(getNumberOfFiltersApplied());
    }, [grainIdFilter, searchQuery, dateRange, centerIdFilter, stateFilter, districtFilter]);

    const initialRef = React.useRef(null);
    const finalRef = React.useRef(null);

    return (
        <View>
            <Modal
                size={"xl"}
                isOpen={filterModalVisible}
                onClose={cancelAndRestoreFilters}
                initialFocusRef={initialRef}
                finalFocusRef={finalRef}>
                <Modal.Content>
                    <Modal.CloseButton />
                    <Modal.Header>{getText('filters')}</Modal.Header>
                    <Modal.Body>
                        <JobFilters />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={2}>
                            <Button variant="ghost" colorScheme="blueGray" onPress={cancelAndRestoreFilters}>
                                {getText('cancel')}
                            </Button>
                            <Button onPress={saveAndHideFilterModal}>{getText('saveFilters')}</Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
            <View style={{
                marginTop: 8,
                marginBottom: 16
            }}>
                <HStack space={2} justifyContent={"center"} flexWrap="nowrap">
                    {!isSelectionMode || !isAveragingEnabled ? (
                        <>
                            <Button
                                size="sm"
                                startIcon={<MaterialIconByName name="filter" />}
                                onPress={showFilterModal}>
                                {getText('filters')}
                            </Button>
                            <Button
                                size="sm"
                                startIcon={<MaterialIconByName name="close" />}
                                onPress={handleReset}>
                                {numberOfFiltersApplied > 0
                                    ? `${getText('reset')} (${numberOfFiltersApplied})` : getText('reset')}
                            </Button>
                            {isAveragingEnabled && (
                                <Button
                                    size="sm"
                                    startIcon={<MaterialIconByName name="vector-combine" />}
                                    onPress={() => dispatch(JobActions.toggleSelectionMode())}
                                    colorScheme="info">
                                    {getText('select')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Button
                                size="sm"
                                startIcon={<MaterialIconByName name="close" />}
                                variant="outline"
                                onPress={handleReset}>
                                {getText('cancel')}
                            </Button>
                            <Button
                                size="sm"
                                startIcon={<MaterialIconByName name="calculator" />}
                                colorScheme="success"
                                onPress={handleAverage}
                                isDisabled={!validationResult.isValid || isCreatingJob}
                                isLoading={isCreatingJob}>
                                {selectedJobIds.length > 0 ? `Average (${selectedJobIds.length})` : 'Average'}
                            </Button>
                        </>
                    )}
                </HStack>
                {(!isSelectionMode || !isAveragingEnabled) && (
                    <ActiveJobFilterChips />
                )}
                {isSelectionMode && isAveragingEnabled && !validationResult.isValid && validationResult.errorMessage && (
                    <Text color="error.500" fontSize="xs" mt={2} textAlign="center">
                        {validationResult.errorMessage}
                    </Text>
                )}
            </View>

        </View>
    );
};
